// RAGDocumentManager.js - Component for managing RAG documents
import React, { useState, useEffect, useRef } from 'react';
import { ragService } from './ragService';
import { useDocumentUpload } from '../../hooks/useDocumentUpload';
import { getCanonicalFolderName } from '../utils/filenameUtils';
import {
  FileText,
  Plus,
  Edit,
  Trash2,
  Save,
  Download,
  Upload,
  AlertCircle,
  Check,
  Loader,
  Eye,
  EyeOff,
  RefreshCw
} from 'lucide-react';

const RAGDocumentManager = ({ itinerary, setItinerary, onStatusChange }) => {
  const [ragManifest, setRagManifest] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingDocument, setEditingDocument] = useState(null);
  const [showRawJson, setShowRawJson] = useState(false);
  const [editingKeyword, setEditingKeyword] = useState(null);
  const [newDocument, setNewDocument] = useState({
    id: '',
    filename: '',
    title: '',
    description: '',
    keywords: [],
    category: '',
    relevantStops: [],
    priority: 1,
    wordCount: 0
  });

  // Document upload functionality
  const fileInputRef = useRef(null);
  const { uploadDocument, deleteDocument: deleteDocumentFile, isUploading, error: uploadError } = useDocumentUpload();
  const [dragActive, setDragActive] = useState(false);

  // Check if RAG is enabled in the itinerary
  const ragEnabled = itinerary?.metadata?.ragDocuments?.enabled;
  const manifestUrl = itinerary?.metadata?.ragDocuments?.manifestUrl;

  useEffect(() => {
    if (ragEnabled && manifestUrl) {
      loadRAGManifest();
    }
  }, [ragEnabled, manifestUrl]);

  const loadRAGManifest = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const manifest = await ragService.loadManifest(itinerary.metadata);
      setRagManifest(manifest);
      setSuccess('RAG manifest loaded successfully');
    } catch (err) {
      if (err.message === 'MANIFEST_NOT_FOUND') {
        // Create a new manifest for this itinerary
        const newManifest = ragService.createNewManifest(itinerary.metadata);
        setRagManifest(newManifest);
        setSuccess('Manifest not found - created new RAG manifest');
      } else {
        setError(`Error loading RAG manifest: ${err.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const generateManifestUrl = () => {
    if (!itinerary?.metadata?.country || (!itinerary?.metadata?.filename && !itinerary?.metadata?.name)) {
      setError('Country and filename or name are required to generate manifest URL');
      return;
    }

    const country = itinerary.metadata.country.toLowerCase().replace(/[^\w]/g, '_').replace(/_+/g, '_');
    const folderPath = getCanonicalFolderName(itinerary.metadata);
    // Use direct Cloud Storage URL format (consistent with ragService.js)
    const manifestUrl = `https://storage.googleapis.com/ai-tourguide-1dc92.firebasestorage.app/special_itineraries/${country}/${folderPath}/rag_documents/manifest.json`;

    // Update the itinerary metadata
    setItinerary(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        ragDocuments: {
          enabled: true,
          manifestUrl
        }
      }
    }));

    setSuccess('RAG manifest URL generated and enabled');
  };

  const formatLanguageCode = (code) => {
    if (!code) return 'en-us';
    const parts = code.split(/[-_]/);
    if (parts.length === 1) {
      const lang = parts[0].toLowerCase();
      return (lang === 'en') ? 'en-us' : `${lang}-${lang}`;
    } else {
      return `${parts[0].toLowerCase()}-${parts[1].toLowerCase()}`;
    }
  };

  const sanitizeItineraryName = (name) => {
    if (!name) return '';
    return name
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[\s'-]+/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
  };

  const addNewDocument = () => {
    if (!newDocument.id || !newDocument.title) {
      setError('Document ID and title are required');
      return;
    }

    const document = {
      ...newDocument,
      keywords: newDocument.keywords.filter(k => k.trim() !== ''),
      relevantStops: newDocument.relevantStops.filter(s => s.trim() !== '')
    };

    setRagManifest(prev => ({
      ...prev,
      documents: [...(prev?.documents || []), document],
      lastUpdated: new Date().toISOString()
    }));

    // Reset form
    setNewDocument({
      id: '',
      filename: '',
      title: '',
      description: '',
      keywords: [],
      category: '',
      relevantStops: [],
      priority: 1,
      wordCount: 0
    });

    setSuccess('Document added successfully');
  };

  const updateDocument = (documentId, updatedDoc) => {
    setRagManifest(prev => ({
      ...prev,
      documents: prev.documents.map(doc =>
        doc.id === documentId ? { ...doc, ...updatedDoc } : doc
      ),
      lastUpdated: new Date().toISOString()
    }));
    setEditingDocument(null);
    setSuccess('Document updated successfully');
  };

  const deleteDocument = (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    setRagManifest(prev => ({
      ...prev,
      documents: prev.documents.filter(doc => doc.id !== documentId),
      lastUpdated: new Date().toISOString()
    }));
    setSuccess('Document deleted successfully');
  };

  const regenerateGlobalKeywords = () => {
    try {
      const updatedManifest = ragService.updateManifestGlobalKeywords(ragManifest);
      setRagManifest(updatedManifest);
      setSuccess(`Global keywords regenerated: ${updatedManifest.globalKeywords.length} keywords found`);
    } catch (err) {
      setError(`Error regenerating keywords: ${err.message}`);
    }
  };

  const updateKeywordTranslation = async (keywordKey, language, label, description) => {
    // Update the manifest state first
    const updatedManifest = {
      ...ragManifest,
      globalKeywords: ragManifest.globalKeywords.map(kw => {
        if (kw.key === keywordKey) {
          const updated = ragService.addKeywordTranslation(kw, language, label, description);
          return updated;
        }
        return kw;
      }),
      lastUpdated: new Date().toISOString()
    };

    setRagManifest(updatedManifest);

    // Auto-save to cloud storage
    setIsLoading(true);
    try {
      await ragService.saveManifest(updatedManifest, itinerary.metadata);
      setSuccess(`Translation for "${keywordKey}" saved successfully to cloud storage`);
    } catch (err) {
      setError(`Translation updated locally but failed to save to cloud: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const saveManifest = async () => {
    setIsLoading(true);
    try {
      // Save manifest to cloud storage using the ragService
      await ragService.saveManifest(ragManifest, itinerary.metadata);
      setSuccess('Manifest saved to cloud storage successfully');
    } catch (err) {
      setError(`Error saving manifest: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const exportManifest = () => {
    try {
      ragService.exportManifest(ragManifest, 'manifest.json');
      setSuccess('Manifest exported successfully');
    } catch (err) {
      setError(`Error exporting manifest: ${err.message}`);
    }
  };

  // File upload handlers
  const handleFileUpload = async (file) => {
    if (!file) return;

    try {
      setError(null);
      const uploadMetadata = {
        country: itinerary?.metadata?.country,
        name: itinerary?.metadata?.name,
        filename: itinerary?.metadata?.filename,
        itineraryName: getCanonicalFolderName(itinerary?.metadata || {})
      };

      const documentUrl = await uploadDocument(file, uploadMetadata);

      // Auto-create a document entry in the manifest
      const newDoc = {
        id: file.name.replace('.json', '').toLowerCase().replace(/[^a-z0-9]/g, '_'),
        filename: file.name,
        title: file.name.replace('.json', ''),
        description: `Document uploaded from ${file.name}`,
        keywords: [],
        category: 'uploaded',
        relevantStops: [],
        priority: 2,
        wordCount: Math.floor(file.size / 5), // Rough estimate
        url: documentUrl
      };

      const updatedManifest = ragService.addDocumentToManifest(ragManifest, newDoc);
      setRagManifest(updatedManifest);
      setSuccess(`Document ${file.name} uploaded successfully`);
    } catch (err) {
      setError(`Error uploading file: ${err.message}`);
    }
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    const jsonFile = files.find(file => file.name.toLowerCase().endsWith('.json'));

    if (jsonFile) {
      handleFileUpload(jsonFile);
    } else {
      setError('Please upload a .json file');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDeleteDocumentFile = async (doc) => {
    try {
      if (doc.url) {
        const uploadMetadata = {
          country: itinerary?.metadata?.country,
          name: itinerary?.metadata?.name,
          filename: itinerary?.metadata?.filename,
          itineraryName: getCanonicalFolderName(itinerary?.metadata || {})
        };
        await deleteDocumentFile(doc.url, uploadMetadata);
      }

      // Remove from manifest
      const updatedManifest = ragService.removeDocumentFromManifest(ragManifest, doc.id);
      setRagManifest(updatedManifest);
      setSuccess(`Document ${doc.filename} deleted successfully`);
    } catch (err) {
      setError(`Error deleting file: ${err.message}`);
    }
  };

  const KeywordTranslationEditor = ({ keyword, onSave, onCancel }) => {
    const [selectedLanguage, setSelectedLanguage] = useState('pt');
    const [label, setLabel] = useState('');
    const [description, setDescription] = useState('');

    const availableLanguages = [
      { code: 'pt', name: 'Portuguese' },
      { code: 'es', name: 'Spanish' },
      { code: 'fr', name: 'French' },
      { code: 'de', name: 'German' },
      { code: 'it', name: 'Italian' },
      { code: 'bg', name: 'Bulgarian' },
      { code: 'nl', name: 'Dutch' },
      { code: 'pl', name: 'Polish' },
      { code: 'ru', name: 'Russian' },
      { code: 'zh', name: 'Chinese' },
      { code: 'ja', name: 'Japanese' },
      { code: 'ko', name: 'Korean' }
    ];

    const handleLanguageChange = (lang) => {
      setSelectedLanguage(lang);
      setLabel(keyword.labels?.[lang] || '');
      setDescription(keyword.descriptions?.[lang] || '');
    };

    const handleSave = () => {
      if (!label.trim() || !description.trim()) {
        alert('Label and description are required');
        return;
      }
      onSave(selectedLanguage, label, description);
    };

    return (
      <div className="border rounded-lg p-4 bg-gray-50">
        <h4 className="font-medium mb-3">Edit Translations for: {keyword.key}</h4>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Language</label>
            <select
              value={selectedLanguage}
              onChange={(e) => handleLanguageChange(e.target.value)}
              className="w-full p-2 border rounded"
            >
              {availableLanguages.map(lang => (
                <option key={lang.code} value={lang.code}>
                  {lang.name} ({lang.code})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Current Translations</label>
            <div className="p-2 border rounded bg-white text-xs">
              {keyword.labels && Object.keys(keyword.labels).length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {Object.keys(keyword.labels).map(lang => (
                    <span key={lang} className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                      {lang}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-gray-500">No translations yet</span>
              )}
            </div>
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">Label *</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="w-full p-2 border rounded"
              placeholder={`Label in ${selectedLanguage}`}
            />
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">Description *</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full p-2 border rounded h-20"
              placeholder={`Description in ${selectedLanguage}`}
            />
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            Save Translation
          </button>
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  const DocumentForm = ({ doc, onSave, onCancel, isEditing = false }) => {
    const [formData, setFormData] = useState(doc || {
      id: '',
      filename: '',
      title: '',
      description: '',
      keywords: [],
      category: '',
      relevantStops: [],
      priority: 1,
      wordCount: 0
    });

    const handleKeywordsChange = (value) => {
      const keywords = value.split(',')
        .map(k => ragService.sanitizeKeyword(k))
        .filter(k => k !== '');
      setFormData(prev => ({ ...prev, keywords }));
    };

    const handleStopsChange = (value) => {
      const stops = value.split(',').map(s => s.trim()).filter(s => s !== '');
      setFormData(prev => ({ ...prev, relevantStops: stops }));
    };

    return (
      <div className="border rounded-lg p-4 bg-gray-50">
        <h4 className="font-medium mb-3">{isEditing ? 'Edit Document' : 'Add New Document'}</h4>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Document ID *</label>
            <input
              type="text"
              value={formData.id}
              onChange={(e) => setFormData(prev => ({ ...prev, id: e.target.value }))}
              className="w-full p-2 border rounded"
              placeholder="e.g., doc_007"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Filename</label>
            <input
              type="text"
              value={formData.filename}
              onChange={(e) => setFormData(prev => ({ ...prev, filename: e.target.value }))}
              className="w-full p-2 border rounded"
              placeholder="e.g., architectural_details.json"
            />
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full p-2 border rounded"
              placeholder="Document title"
            />
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full p-2 border rounded h-20"
              placeholder="Brief description of the document content"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Keywords (comma-separated)</label>
            <textarea
              value={formData.keywords.join(', ')}
              onChange={(e) => handleKeywordsChange(e.target.value)}
              className="w-full p-2 border rounded h-20"
              placeholder="keyword1, keyword2, keyword3"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
              className="w-full p-2 border rounded"
            >
              <option value="">Select category</option>
              <option value="architecture">Architecture</option>
              <option value="history">History</option>
              <option value="art">Art</option>
              <option value="conservation">Conservation</option>
              <option value="activities">Activities</option>
              <option value="culture">Culture</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Relevant Stops (comma-separated)</label>
            <input
              type="text"
              value={formData.relevantStops.join(', ')}
              onChange={(e) => handleStopsChange(e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="stop_id_1, stop_id_2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Priority</label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) }))}
              className="w-full p-2 border rounded"
            >
              <option value={1}>High (1)</option>
              <option value={2}>Medium (2)</option>
              <option value={3}>Low (3)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Word Count</label>
            <input
              type="number"
              value={formData.wordCount}
              onChange={(e) => setFormData(prev => ({ ...prev, wordCount: parseInt(e.target.value) || 0 }))}
              className="w-full p-2 border rounded"
              placeholder="0"
            />
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <button
            onClick={() => onSave(formData)}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {isEditing ? 'Update' : 'Add'} Document
          </button>
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="mt-6 pt-6 border-t">
      <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
        <FileText className="w-5 h-5" />
        RAG Document Management
      </h3>

      {/* RAG Status and Controls */}
      <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex justify-between items-start">
          <div>
            <h4 className="font-medium text-blue-900">RAG Status</h4>
            <p className="text-sm text-blue-700 mt-1">
              {ragEnabled ? '✅ RAG documents are enabled' : '❌ RAG documents are disabled'}
            </p>
            {manifestUrl && (
              <p className="text-xs text-blue-600 mt-1 break-all">
                Manifest URL: {manifestUrl}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            {!ragEnabled && (
              <button
                onClick={generateManifestUrl}
                className="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Enable RAG
              </button>
            )}
            {ragEnabled && manifestUrl && (
              <button
                onClick={loadRAGManifest}
                disabled={isLoading}
                className="px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600 text-sm flex items-center gap-1"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                Reload
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded flex items-center gap-2 text-red-700">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded flex items-center gap-2 text-green-700">
          <Check className="w-4 h-4" />
          {success}
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded flex items-center gap-2">
          <Loader className="w-4 h-4 animate-spin" />
          Loading RAG manifest...
        </div>
      )}

      {/* RAG Manifest Content */}
      {ragManifest && (
        <div className="space-y-4">
          {/* Manifest Info */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex justify-between items-start mb-2">
              <h4 className="font-medium">Manifest Information</h4>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowRawJson(!showRawJson)}
                  className="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1"
                >
                  {showRawJson ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  {showRawJson ? 'Hide' : 'Show'} Raw JSON
                </button>
                <button
                  onClick={saveManifest}
                  disabled={isLoading}
                  className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm flex items-center gap-1 disabled:opacity-50"
                >
                  <Upload className="w-4 h-4" />
                  Save to Cloud
                </button>
                <button
                  onClick={exportManifest}
                  className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm flex items-center gap-1"
                >
                  <Download className="w-4 h-4" />
                  Export Locally
                </button>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <strong>Itinerary ID:</strong> {ragManifest.itineraryId}
              </div>
              <div>
                <strong>Name:</strong> {ragManifest.itineraryName}
              </div>
              <div>
                <strong>Last Updated:</strong> {new Date(ragManifest.lastUpdated).toLocaleDateString()}
              </div>
              <div>
                <strong>Documents:</strong> {ragManifest.documents?.length || 0}
              </div>
              <div>
                <strong>Global Keywords:</strong> {ragManifest.globalKeywords?.length || 0}
              </div>
              <div className="flex items-center">
                <button
                  onClick={regenerateGlobalKeywords}
                  className="px-2 py-1 bg-purple-500 text-white rounded hover:bg-purple-600 text-xs flex items-center gap-1"
                  title="Regenerate global keywords from all documents"
                >
                  <RefreshCw className="w-3 h-3" />
                  Regen Keywords
                </button>
              </div>
            </div>

            {showRawJson && (
              <div className="mt-4">
                <pre className="bg-gray-800 text-green-400 p-3 rounded text-sm overflow-auto max-h-60">
                  {JSON.stringify(ragManifest, null, 2)}
                </pre>
              </div>
            )}
          </div>

          {/* File Upload Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h4 className="font-medium mb-3 text-blue-800">📁 Upload Document Files</h4>

            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                dragActive
                  ? 'border-blue-500 bg-blue-100'
                  : 'border-gray-300 hover:border-blue-400'
              }`}
              onDrop={handleFileDrop}
              onDragOver={(e) => {
                e.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={() => setDragActive(false)}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleFileSelect}
                className="hidden"
              />

              <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />

              <p className="text-sm text-gray-600 mb-2">
                Drag and drop .json files here, or{' '}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="text-blue-600 hover:text-blue-800 underline"
                  disabled={isUploading}
                >
                  browse files
                </button>
              </p>

              <p className="text-xs text-gray-500">
                Only .json files up to 1MB are supported
              </p>

              {isUploading && (
                <div className="mt-3 flex items-center justify-center gap-2">
                  <Loader className="w-4 h-4 animate-spin" />
                  <span className="text-sm text-blue-600">Uploading...</span>
                </div>
              )}

              {uploadError && (
                <div className="mt-3 text-sm text-red-600 flex items-center justify-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {uploadError}
                </div>
              )}
            </div>

            <p className="text-xs text-gray-600 mt-2">
              💡 Uploaded files will be stored in the same folder as manifest.json and automatically added to the document list below.
            </p>
          </div>

          {/* Add New Document Form */}
          <DocumentForm
            doc={newDocument}
            onSave={(formData) => {
              setNewDocument(formData);
              addNewDocument();
            }}
            onCancel={() => setNewDocument({
              id: '',
              filename: '',
              title: '',
              description: '',
              keywords: [],
              category: '',
              relevantStops: [],
              priority: 1,
              wordCount: 0
            })}
          />

          {/* Existing Documents */}
          <div>
            <h4 className="font-medium mb-3">Existing Documents ({ragManifest.documents?.length || 0})</h4>
            <div className="space-y-3">
              {ragManifest.documents?.map((doc) => (
                <div key={doc.id} className="border rounded-lg p-4">
                  {editingDocument === doc.id ? (
                    <DocumentForm
                      doc={doc}
                      onSave={(updatedDoc) => updateDocument(doc.id, updatedDoc)}
                      onCancel={() => setEditingDocument(null)}
                      isEditing={true}
                    />
                  ) : (
                    <>
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h5 className="font-medium">{doc.title}</h5>
                          <p className="text-sm text-gray-600">ID: {doc.id} | Category: {doc.category} | Priority: {doc.priority}</p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setEditingDocument(doc.id)}
                            className="p-1 text-blue-600 hover:text-blue-800"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteDocumentFile(doc)}
                            className="p-1 text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <p className="text-sm text-gray-700 mb-2">{doc.description}</p>

                      <div className="grid grid-cols-2 gap-4 text-xs">
                        <div>
                          <strong>Keywords:</strong> {doc.keywords?.join(', ') || 'None'}
                        </div>
                        <div>
                          <strong>Relevant Stops:</strong> {doc.relevantStops?.join(', ') || 'None'}
                        </div>
                        <div>
                          <strong>Filename:</strong> {doc.filename || 'Not specified'}
                        </div>
                        <div>
                          <strong>Word Count:</strong> {doc.wordCount || 0}
                        </div>
                        {doc.url && (
                          <div className="col-span-2">
                            <strong>File URL:</strong>{' '}
                            <a
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 underline text-xs break-all"
                            >
                              {doc.url}
                            </a>
                          </div>
                        )}
                      </div>

                      {/* Keyword Translations for this Document */}
                      {doc.keywords && doc.keywords.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <h6 className="text-sm font-semibold mb-2 text-purple-700">Keyword Translations:</h6>
                          <div className="space-y-2">
                            {doc.keywords.map(keyword => {
                              const globalKeyword = ragManifest.globalKeywords?.find(gk => gk.key === keyword);
                              return (
                                <div key={keyword} className="bg-purple-50 rounded p-2">
                                  {editingKeyword === keyword ? (
                                    <KeywordTranslationEditor
                                      keyword={globalKeyword || { key: keyword, labels: { en: keyword }, descriptions: { en: `Related to ${keyword}` } }}
                                      onSave={async (language, label, description) => {
                                        await updateKeywordTranslation(keyword, language, label, description);
                                        setEditingKeyword(null);
                                      }}
                                      onCancel={() => setEditingKeyword(null)}
                                    />
                                  ) : (
                                    <div className="flex justify-between items-start">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className="font-mono text-xs font-semibold text-purple-900">{keyword}</span>
                                          <button
                                            onClick={() => setEditingKeyword(keyword)}
                                            className="text-purple-600 hover:text-purple-800"
                                            title="Add/Edit translations"
                                          >
                                            <Edit className="w-3 h-3" />
                                          </button>
                                        </div>
                                        {globalKeyword?.labels && (
                                          <div className="flex flex-wrap gap-1 text-xs">
                                            {Object.entries(globalKeyword.labels).map(([lang, label]) => (
                                              <span key={lang} className="bg-purple-200 text-purple-800 px-2 py-0.5 rounded">
                                                {lang}: {label}
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                        {!globalKeyword && (
                                          <span className="text-xs text-gray-500 italic">No translations yet - click edit to add</span>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}

              {(!ragManifest.documents || ragManifest.documents.length === 0) && (
                <div className="text-center py-8 text-gray-500">
                  No documents found. Add your first document above.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RAGDocumentManager;
